import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { BriefcaseMedical } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PROVIDER_SETTING_TYPE, PROVIDER_SEARCH_TYPES } from "@/constants";
import { useProviderSearchSetting } from "@/hooks/use-provider-search";
import type { ProviderSearchSettingType } from "@/model/provider-search";
import SettingsIconSkeletonLoader from "./settings-icon-skeleton-loader";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import {
  setCategory,
  setLocation,
  setProviderSearchDynamicPayload,
  setProviderSearchType,
  setSearchCriteria,
  setSpecialty,
  setSpecialtyList,
  setTaxonomyCode,
} from "@/store/provider-search/provider-search-slice";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useUserLocation } from "@/context/location-provider";

const QuickSearch: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    location: userLocation,
    isFetching: isLocationFetching,
    isFetched: isLocationFetched,
  } = useUserLocation();
  const {
    data: settingData,
    isFetching: isSettingFetching,
    isFetched: isSettingFetched,
  } = useProviderSearchSetting();

  useEffect(() => {
    if (userLocation?.lat && userLocation.lng) {
      dispatch(
        setLocation({
          description: userLocation.description,
          lat: userLocation.lat,
          lng: userLocation.lng,
          place_id: userLocation.place_id,
          reference: "",
        }),
      );
    }
  }, [userLocation?.lat, userLocation?.lng, dispatch, userLocation]);

  const quickSearchSettings = useMemo(() => {
    return settingData?.filter(
      (quickSearchSetting) =>
        quickSearchSetting.type === PROVIDER_SETTING_TYPE.QUICK_SEARCH,
    );
  }, [settingData]);

  const onQuickSearch = (quickSearchSetting: ProviderSearchSettingType) => {
    dispatch(
      setSpecialty({
        searchParameter: "",
        specialtyDesc: "",
        type: "",
      }),
    );
    dispatch(setSpecialtyList([]));
    dispatch(setProviderSearchType(PROVIDER_SEARCH_TYPES.QUICK_SEARCH));
    dispatch(
      setProviderSearchDynamicPayload({
        category: quickSearchSetting.categoryCode,
        searchCriteria: quickSearchSetting?.wildcardQuickSearchText,
        taxonomyCode: quickSearchSetting?.taxonomyCode,
      }),
    );
    dispatch(setCategory(quickSearchSetting));
    dispatch(setSearchCriteria(quickSearchSetting?.wildcardQuickSearchText));
    dispatch(setTaxonomyCode(quickSearchSetting?.taxonomyCode));
    const targetPath = `/${ScreenRouteNames.PROVIDER_SEARCH_BY_GROUP.path}/${ScreenRouteNames.PROVIDER_SEARCH_RESULT.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const renderQuickSearchSection = () => {
    if (isSettingFetching || isLocationFetching) {
      return <SettingsIconSkeletonLoader numberOfPlaceholder={5} />;
    } else if (isSettingFetched && isLocationFetched) {
      if (
        (settingData as ProviderSearchSettingType[])?.length > 0 &&
        quickSearchSettings
      ) {
        return quickSearchSettings?.map((quickSearchSetting, _) => {
          return (
            /* 2.1.1 Keyboard — A / 4.1.2 — A: <Card> renders a plain <div>, so these
               quick-search category tiles were unreachable by keyboard and announced as
               static text. <Title> also emitted an <h1> at the time, which would have put a
               level-one heading inside a control. Title now takes an explicit level, but
               a tile label is not a heading at any level — a <span> carries the same
               visual weight. */
            <button
              type="button"
              key={`quick-search-${quickSearchSetting.categoryCode}`}
              className="flex h-[110px] w-[110px] cursor-pointer items-center justify-center rounded-lg border bg-card text-card-foreground shadow hover:bg-primary-extraLight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onClick={() => onQuickSearch(quickSearchSetting)}
            >
              <div className="flex flex-col items-center p-2 text-center">
                <img
                  className="mb-4"
                  id={`provider-quick-search-icon-${quickSearchSetting.categoryCode}`}
                  src={`data:image/svg+xml;base64,${quickSearchSetting.icon}`}
                  height={32}
                  width={32}
                  alt=""
                />
                <span className="mb-1 text-sm font-medium leading-normal">
                  {quickSearchSetting?.displayName}
                </span>
              </div>
            </button>
          );
        });
      } else {
        return (
          <NoDataAvailable
            message="There are currently no quick search to display."
            icon={BriefcaseMedical}
          />
        );
      }
    } else {
      return <SomethingWentWrong />;
    }
  };
  return renderQuickSearchSection();
};

export default QuickSearch;
